import { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/lib/auth-context";
import { supabase } from "@/integrations/supabase/client";
import { CURRENT_PRIVACY_POLICY_VERSION } from "@/lib/privacy-policy";
import { ShieldCheck, Loader2 } from "lucide-react";
import { toast } from "sonner";

export default function PrivacyPolicyConsentDialog() {
  const { user, loading } = useAuth();
  const location = useLocation();
  const queryClient = useQueryClient();
  const [saving, setSaving] = useState(false);

  const { data, isLoading } = useQuery({
    queryKey: ["privacy-consent", user?.id],
    enabled: !!user && !loading,
    staleTime: 60_000,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("profiles")
        .select("privacy_policy_version")
        .eq("user_id", user!.id)
        .single();
      if (error) throw error;
      return data;
    },
  });

  const exemptPath =
    location.pathname.startsWith("/polityka-prywatnosci") ||
    location.pathname.startsWith("/profile");

  const needsConsent =
    !!user &&
    !isLoading &&
    !exemptPath &&
    data != null &&
    data.privacy_policy_version !== CURRENT_PRIVACY_POLICY_VERSION;

  const accept = async () => {
    if (!user) return;
    setSaving(true);
    const { error } = await supabase
      .from("profiles")
      .update({
        privacy_policy_version: CURRENT_PRIVACY_POLICY_VERSION,
        privacy_policy_accepted_at: new Date().toISOString(),
      })
      .eq("user_id", user.id);
    setSaving(false);

    if (error) {
      toast.error("Nie udało się zapisać zgody. Spróbuj ponownie.");
      return;
    }
    queryClient.setQueryData(["privacy-consent", user.id], {
      privacy_policy_version: CURRENT_PRIVACY_POLICY_VERSION,
    });
  };

  if (!needsConsent) return null;

  return (
    <AlertDialog open>
      <AlertDialogContent onEscapeKeyDown={(e) => e.preventDefault()}>
        <AlertDialogHeader>
          <div className="flex items-center gap-2">
            <ShieldCheck className="h-5 w-5 text-primary" />
            <AlertDialogTitle>Zaktualizowaliśmy Politykę Prywatności</AlertDialogTitle>
          </div>
          <AlertDialogDescription asChild>
            <div className="space-y-2 text-sm text-muted-foreground">
              <p>
                9 września 2026 r. weszła w życie nowa wersja Polityki Prywatności. Aby dalej korzystać
                z konta, zapoznaj się z nią i potwierdź akceptację.
              </p>
              <p>
                Pełna treść:{" "}
                <Link
                  to="/polityka-prywatnosci"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="font-medium text-primary underline"
                >
                  Polityka Prywatności
                </Link>
              </p>
            </div>
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <Button onClick={accept} disabled={saving} className="w-full sm:w-auto">
            {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Akceptuję nową Politykę Prywatności
          </Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
